// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Action from 'core/fsmpack/statemachine/actions/Action';
import SystemBus from 'core/entities/SystemBus';
import SpeechManager from 'aws/polly/SpeechManager';
import Speech from 'aws/polly/Speech';
import SpeechComponent from 'core/awspack/polly/SpeechComponent';

// spell-checker: words Cognito cognito ssml

/**
 * @autodoc apply FsmAction
 */
class StartSpeechAction extends Action {

  constructor(options) {
    super(...arguments); // also support (id, settings) parameters used with FSM
    this._waitingToStart = false;
  }

  static getTransitionLabel(transitionKey, actionConfig) {
    return 'On Speech End';
  }

  enter(fsm) {
    const entity = fsm.getOwnerEntity();
    const speechManager = SpeechManager.getInstance();
    if (!entity || !speechManager) {
      return;
    }

    if (speechManager.ready) {
      this._startSpeech(fsm, entity);
    } else {
      if(entity.world.getSystem('AwsSystem').cognitoIdentityPoolId) {
        // defer speech start by dynamically creating an update() function to handle the start
        // when the AWS SDK is ready. The update() function then removes itself once the speech
        // is started. In the future if we need update() to do anything other than deferred
        // starting, we'll have to refactor update() in to a normal class function
        this._waitingToStart = true;
        this.update = (fsm) => {
          if(this._waitingToStart && speechManager.ready) {
            // deferred start after AwsSystem.SDK_READY is received
            this._waitingToStart = false;
            this._startSpeech(fsm, entity);

            // remove the update function as an optimization, since we no longer need it for deferred
            // starting.   In the future, if we need update() for anything other than the
            // deferred start, remove this statement
            delete this.update;
          }
        };
      } else {
        SystemBus.emit('sumerian.warning', {
          title: `Amazon Cognito Pool ID Missing`,
          message: 'The Cognito Identity Pool ID in the AWS configuration panel on the root scene node is required for Speech. Please enter a Cognito Identity Pool ID with the correct permissions.'
        });
      }
    }
  }

  _startSpeech(fsm, entity) {
    if (!entity.hasComponent('SpeechComponent')) {
      entity.setComponent(new SpeechComponent());
    }

    if (this.useLex) {
      return this._playLexResponse(fsm);
    }

    fsm.getWorld().loader.load(this.speech).then((speech) => {
      entity.speechComponent.addSpeech(speech);

      this._playSpeech(fsm, speech);
    });
  }

  _playLexResponse(fsm) {
    const entity = fsm.getOwnerEntity();
    const speechComponent = entity.getComponent('SpeechComponent');

    const speech = new Speech();
    speech.body = entity.getData('lexResponse');
    speech.type = 'ssml';

    speechComponent.addSpeech(speech);

    this._playSpeech(fsm, speech);
  }

  _playSpeech(fsm, speech) {
    speech.play().then(() => {
        if (this._isEntered) {
          fsm.send(this.transitions.complete);
        }
      }).catch((error) => {
        console.error(`[Sumerian] Could not play speech: ${error}`);
      });
  }
}

StartSpeechAction.external = {
  key: 'StartSpeech',
  type: 'Speech',
  name: 'Start Speech',
  description: 'Starts speech with a text or ssml file.',
  canTransition: true,
  parameters: [
    {
      name: 'Speech',
      key: 'speech',
      type: 'speech',
      description: 'Speech to play.',
    },
    {
      name: 'Use Lex Response',
      key: 'useLex',
      type: 'boolean',
      description: 'Use response from Dialogue bot',
    },
  ],
  transitions: [
    {
      key: 'complete',
      description: 'State to transition to when the speech stops playing.',
    },
  ],
};

StartSpeechAction.register();

export default StartSpeechAction;
